/**
 * The delivery beacon for readers still streaming when the page goes away.
 *
 * chatbotApi reports a stream's end from inside the stream: the normal close
 * after the read loop, the abort from its `catch`. Closing the tab runs
 * neither — the document is destroyed with the reader's promise still pending
 * — so a turn the person walked away from left no row at all. Measured
 * 2026-09-14: 155 prod beacons after `abort_reason` shipped, zero `unknown`,
 * the label that was meant to catch tab closes.
 *
 * That departure is the one the delivery rate most needs. Leaving after three
 * seconds is someone changing their mind; leaving after ninety seconds of
 * nothing is our failure. Only `elapsed_ms` and `last_event_id` on a row that
 * EXISTS can tell them apart.
 *
 * Pure, so it tests without the store and PostHog that chatbotApi imports.
 */

export interface InFlightDelivery {
  /** The server's stream turn id — null until the first event carries it. */
  turnId: string | null;
  /** Client-minted per send, so a reader that has seen no event still has a key. */
  requestId: string;
  /** Browser wall clock at send start; stable across every close path. */
  attemptStartedAtMs: number;
  /** Auth/workspace context frozen when this reader began. */
  requestHeaders: Record<string, string>;
  answerRendered: boolean;
  answerChars: number;
  lastEventId: number;
  elapsedMs: number;
}

export interface PageExitBeacon {
  conversationId: string;
  /** The reader reported, so the caller can label it before its own catch runs. */
  controller: AbortController;
  requestHeaders: Record<string, string>;
  payload: Record<string, unknown>;
}

/**
 * The key a beacon files under. The stream turn id when there is one. Before
 * the first event there is none, and the endpoint drops a beacon without a key
 * — which erased precisely the departures that happen while nothing has
 * arrived. The request id is id-shaped and unique per send: it cannot join to
 * StreamExit, but the row still counts.
 */
export function beaconTurnId(turnId: string | null, requestId: string): string {
  return turnId || requestId;
}

/**
 * The id of one typed message, minted when the person presses Send and
 * carried by every attempt it takes — the failed POST, the Retry, the replay
 * after a Reconnect reload. The server keys the delivery rate on it, and
 * drops any value outside `send_<13 digits>_<6–16 of [a-z0-9]>`, so the
 * random part is fixed-width hex rather than a base-36 slice that can come up
 * short.
 */
export function mintSendId(nowMs: number = Date.now()): string {
  return mintDeliveryId('send', nowMs);
}

/** `<prefix>_<13-digit ms>_<12 hex>` — the shape both delivery id checks accept. */
export function mintDeliveryId(prefix: 'send' | 'req', nowMs: number = Date.now()): string {
  let suffix = '';
  try {
    const bytes = new Uint8Array(6);
    globalThis.crypto.getRandomValues(bytes);
    suffix = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    suffix = Math.floor(Math.random() * 0xffffffffffff).toString(16).padStart(12, '0');
  }
  return `${prefix}_${String(Math.max(0, Math.floor(nowMs))).padStart(13, '0').slice(-13)}_${suffix}`;
}

/** Shared identity for every way one browser attempt can end. */
export function deliveryBeaconIdentity(
  turnId: string | null,
  requestId: string,
  attemptStartedAtMs: number,
): Record<string, string | number> {
  return {
    turn_id: beaconTurnId(turnId, requestId),
    attempt_id: requestId,
    attempt_started_at_ms: attemptStartedAtMs,
  };
}

/** bfcache pagehide is a suspension; the same reader can resume and finish. */
export function shouldReportPageExit(persisted: boolean): boolean {
  return !persisted;
}

export type PageNavigationType = 'navigate' | 'reload' | 'back_forward' | 'prerender';

/**
 * How THIS document was reached — the one witness to why the previous one died.
 *
 * A page destroyed mid-turn leaves no reason behind: nothing on the server
 * precedes a reload, and the dying page cannot tell a refresh from a navigation.
 * The next document can. Riding on every beacon, this separates "the page was
 * reloaded" from "the person navigated here" after the fact. Undefined when the
 * browser exposes no navigation entry, so the server records absence rather
 * than a guess.
 */
export function pageNavigationType(
  entries: ReadonlyArray<{ type?: string }> | undefined =
    typeof performance !== 'undefined' && typeof performance.getEntriesByType === 'function'
      ? (performance.getEntriesByType('navigation') as unknown as ReadonlyArray<{ type?: string }>)
      : undefined,
): PageNavigationType | undefined {
  const type = entries?.[0]?.type;
  return type === 'navigate' || type === 'reload' || type === 'back_forward' || type === 'prerender'
    ? type
    : undefined;
}

/**
 * A stream that reached its terminal event with nothing rendered should be
 * rebuilt from the persisted conversation. The turn finished — the answer is
 * saved — but this reader never received it: a reattach whose replay skipped
 * the answer, or a stream key that expired first. Painting nothing leaves the
 * expanded reasoning standing in for a reply, which reads as Leo having
 * answered with its own thinking.
 */
export function shouldRehydrateAfterClose(
  streamDropped: boolean,
  answerRendered: boolean,
): boolean {
  return !streamDropped && !answerRendered;
}

/** A recoverable drop is not the end of the browser attempt yet. */
export function streamCloseOutcome(
  streamDropped: boolean,
  willReconnect: boolean,
): 'dropped' | 'terminal' | null {
  if (streamDropped && willReconnect) return null;
  return streamDropped ? 'dropped' : 'terminal';
}

export function pageExitBeacons(
  readers: Iterable<[string, AbortController]>,
  inFlight: WeakMap<AbortController, () => InFlightDelivery>,
): PageExitBeacon[] {
  const beacons: PageExitBeacon[] = [];
  for (const [conversationId, controller] of readers) {
    // A reader with no snapshot is one that never reports its close at all.
    const read = inFlight.get(controller);
    if (!read) continue;
    let delivery: InFlightDelivery;
    try {
      delivery = read();
    } catch {
      // One broken reader must not cost the others their beacon.
      continue;
    }
    beacons.push({
      conversationId,
      controller,
      requestHeaders: delivery.requestHeaders,
      payload: {
        ...deliveryBeaconIdentity(
          delivery.turnId,
          delivery.requestId,
          delivery.attemptStartedAtMs,
        ),
        // A retry normally rejoins the same server turn_id. Keep the unique
        // client send id too so an interrupted attempt and its successful
        // retry become two immutable events instead of one overwriting the
        // other in the per-turn summary row.
        outcome: 'aborted',
        abort_reason: 'page_unload',
        answer_rendered: delivery.answerRendered,
        answer_chars: delivery.answerChars,
        last_event_id: delivery.lastEventId,
        elapsed_ms: delivery.elapsedMs,
      },
    });
  }
  return beacons;
}
